import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { Observable, Subject } from 'rxjs';
import { takeUntil, filter } from 'rxjs/operators';


import { HeaderService } from '../../services/header.service';
import { WorkspaceService } from '../../services/workspace.service';
import { EditWorkspaceComponent } from './edit-workspace/edit-workspace.component';
import { AppState } from '../../state/app.state';
import * as WorkspaceActions from '../../state/workspace/workspace.actions';
import * as WorkspaceSelectors from '../../state/workspace/workspace.selectors';
import { Workspace, WorkspaceFile, WORKSPACE_MAX_SIZE_BYTES } from '../../models/workspace.model';
import { DeleteWorkspaceModalComponent } from "./delete-workspace-modal/delete-workspace-modal.component";
import {PopupService} from "../../services/popup.service";

interface DeleteFileState {
  show: boolean;
  file: WorkspaceFile | null;
  isDeleting: boolean;
}

interface DeleteWorkspaceState {
  show: boolean;
  isDeleting: boolean;
}

@Component({
  selector: 'app-workspace',
  templateUrl: './workspace.component.html',
  styleUrl: './workspace.component.scss'
})
export class WorkspaceComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  workspaceId: string | null = null;
  workspace$: Observable<Workspace | null>;
  loading$: Observable<boolean>;
  error$: Observable<string | null>;

  showEditModal = false;
  editSaving = false;
  loadingFiles = false;

  // Delete modal state
  deleteFileState: DeleteFileState = {
    show: false,
    file: null,
    isDeleting: false
  };

  // Delete workspace modal state
  deleteWorkspaceState: DeleteWorkspaceState = {
    show: false,
    isDeleting: false
  };

  // Local state for computed values
  workspace: Workspace | null = null;
  workspaceFiles: WorkspaceFile[] = [];
  knowledgeBaseUsage = 0;
  totalFileSize = 0;
  readonly maxFileSize = WORKSPACE_MAX_SIZE_BYTES;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private headerService: HeaderService,
    private store: Store<AppState>,
    private actions$: Actions,
    private workspaceService: WorkspaceService,
    private popupService: PopupService
  ) {
    this.workspace$ = this.store.select(WorkspaceSelectors.selectSelectedWorkspace);
    this.loading$ = this.store.select(WorkspaceSelectors.selectWorkspacesLoading);
    this.error$ = this.store.select(WorkspaceSelectors.selectWorkspacesError);
  }

  ngOnInit(): void {
    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe(params => {
      const id = params.get('id');
      if (id) {
        this.workspaceId = id;
        this.loadWorkspace();
        this.loadWorkspaceFiles(); // Load files separately
      } else {
        this.router.navigate(['/workspaces']);
      }
    });

    // Subscribe to workspace data
    this.workspace$.pipe(
      filter(workspace => workspace !== null),
      takeUntil(this.destroy$)
    ).subscribe(workspace => {
      if (workspace) {
        this.workspace = workspace;
        this.headerService.setHeaderState(workspace.name, null, false);
      }
    });

    // Subscribe to delete success
    this.actions$.pipe(
      ofType(WorkspaceActions.deleteFileSuccess),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.deleteFileState = {
        show: false,
        file: null,
        isDeleting: false
      };

      // Show success notification
      this.popupService.notify('File has been successfully deleted!', 'File deleted', 'success');

      // Reload only files, not the entire workspace
      this.loadWorkspaceFiles();
    });

    // Subscribe to delete failure
    this.actions$.pipe(
      ofType(WorkspaceActions.deleteFileFailure),
      takeUntil(this.destroy$)
    ).subscribe((action) => {
      this.deleteFileState.isDeleting = false;
      this.popupService.notify('Failed to delete file. Please try again.', 'Delete failed', 'error');
    });

    // Subscribe to workspace delete success
    this.actions$.pipe(
      ofType(WorkspaceActions.deleteWorkspaceSuccess),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      // Navigation is handled in effects
      this.deleteWorkspaceState = {
        show: false,
        isDeleting: false
      };
    });

    // Subscribe to workspace delete failure
    this.actions$.pipe(
      ofType(WorkspaceActions.deleteWorkspaceFailure),
      takeUntil(this.destroy$)
    ).subscribe((action) => {
      this.deleteWorkspaceState.isDeleting = false;
      this.popupService.notify('Failed to delete workspace. Please try again.', 'Delete failed', 'error');
    });

    // Subscribe to workspace update success — close edit modal and notify
    this.actions$.pipe(
      ofType(WorkspaceActions.updateWorkspaceSuccess),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.editSaving = false;
      this.showEditModal = false;
      this.popupService.notify('Workspace has been successfully updated!', 'Workspace updated', 'success');
    });

    // Subscribe to workspace update failure — keep modal open and show error
    this.actions$.pipe(
      ofType(WorkspaceActions.updateWorkspaceFailure),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.editSaving = false;
      this.popupService.notify('Failed to update workspace. Please try again.', 'Update failed', 'error');
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    // Clear selected workspace when leaving
    this.store.dispatch(WorkspaceActions.clearSelectedWorkspace());
  }

  private loadWorkspace(): void {
    if (this.workspaceId) {
      this.store.dispatch(WorkspaceActions.loadWorkspaceById({
        id: this.workspaceId,
        includeFiles: false // Don't include files in workspace load
      }));
    }
  }

  private loadWorkspaceFiles(): void {
    if (this.workspaceId) {
      this.loadingFiles = true;
      this.workspaceService.getWorkspaceFiles(this.workspaceId).subscribe({
        next: (response) => {
          this.workspaceFiles = response.files;
          this.loadingFiles = false;
          this.calculateKnowledgeBaseUsage();
        },
        error: (error) => {
          this.loadingFiles = false;
          this.popupService.notify('Failed to load files', 'Error', 'error');
        }
      });
    }
  }

  private calculateKnowledgeBaseUsage(): void {
    this.totalFileSize = this.workspaceFiles.reduce((sum, file) => sum + (file.file_size || 0), 0);
    this.knowledgeBaseUsage = Math.min(100, Math.round((this.totalFileSize / this.maxFileSize) * 100));
  }

  get formattedUsedSize(): string {
    return this.formatBytes(this.totalFileSize);
  }

  get formattedMaxSize(): string {
    return this.formatBytes(this.maxFileSize);
  }

  get isNearLimit(): boolean {
    return this.knowledgeBaseUsage >= 80;
  }

  get isAtLimit(): boolean {
    return this.knowledgeBaseUsage >= 100;
  }

  private formatBytes(bytes: number): string {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  }

  goBack(): void {
    this.router.navigate(['/workspaces']);
  }

  deleteFile(file: WorkspaceFile): void {
    this.deleteFileState = {
      show: true,
      file: file,
      isDeleting: false
    };
  }

  confirmDeleteFile(): void {
    if (this.workspaceId && this.deleteFileState.file) {
      this.deleteFileState.isDeleting = true;
      this.store.dispatch(WorkspaceActions.deleteFileFromWorkspace({
        workspaceId: this.workspaceId,
        fileId: this.deleteFileState.file.id,
        fileName: this.deleteFileState.file.original_filename
      }));
    }
  }

  cancelDeleteFile(): void {
    this.deleteFileState = {
      show: false,
      file: null,
      isDeleting: false
    };
  }

  editWorkspace(): void {
    this.showEditModal = true;
  }

  closeEditModal(): void {
    if (this.editSaving) {
      return;
    }
    this.showEditModal = false;
  }

  saveWorkspace(updatedData: any): void {
    if (!this.workspaceId) {
      return;
    }
    this.editSaving = true;
    this.store.dispatch(WorkspaceActions.updateWorkspace({
      id: this.workspaceId,
      changes: {
        name: updatedData.name,
        description: updatedData.description,
        instructions: updatedData.instructions
      }
    }));
  }

  onFilesUpdated(): void {
    // Reload files when they are updated from the edit modal
    this.loadWorkspaceFiles();
  }

  getFileType(file: WorkspaceFile): string {
    const extension = file.extension.toLowerCase();
    if (['pdf'].includes(extension)) return 'pdf';
    if (['xls', 'xlsx'].includes(extension)) return 'xlsx';
    if (['ppt', 'pptx'].includes(extension)) return 'pptx';
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(extension)) return 'image';
    return 'document';
  }

  canViewFile(file: WorkspaceFile): boolean {
    const viewableTypes = ['pdf', 'image'];
    return viewableTypes.includes(this.getFileType(file));
  }

  viewFile(file: WorkspaceFile): void {
    // Use authenticated endpoint to download file
    this.workspaceService.downloadFileBlob(this.workspaceId!, file.id).subscribe({
      next: (blob) => {
        // Create blob URL for viewing
        const blobUrl = window.URL.createObjectURL(blob);

        // Open in new tab
        const newWindow = window.open(blobUrl, '_blank');

        // Clean up blob URL after a delay (to ensure the tab has loaded)
        setTimeout(() => {
          window.URL.revokeObjectURL(blobUrl);
        }, 1000);
      },
      error: () => {
        this.popupService.notify('Failed to view file. Please try again.', 'View failed', 'error');
      }
    });
  }

  downloadFile(file: WorkspaceFile): void {
    // Download through authenticated API endpoint
    this.workspaceService.downloadFileBlob(this.workspaceId!, file.id).subscribe({
      next: (blob) => {
        // Create blob URL and trigger download
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = file.original_filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      },
      error: () => {
        this.popupService.notify('Failed to download file. Please try again.', 'Download failed', 'error');
      }
    });
  }

  getFileUpdatedAt(file: WorkspaceFile): string {
    const date = new Date(file.created_at);
    const now = new Date();

    // Calculate difference in milliseconds
    const diffMs = now.getTime() - date.getTime();

    // Convert to seconds, minutes, hours, days
    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);

    // Less than a minute
    if (diffSeconds < 60) {
      return 'just now';
    }

    // Less than an hour
    if (diffMinutes < 60) {
      return diffMinutes === 1 ? '1 minute ago' : `${diffMinutes} minutes ago`;
    }

    // Less than a day
    if (diffHours < 24) {
      return diffHours === 1 ? '1 hour ago' : `${diffHours} hours ago`;
    }

    // Check if it's today (same calendar day)
    const dateDay = date.getDate();
    const nowDay = now.getDate();
    const dateMonth = date.getMonth();
    const nowMonth = now.getMonth();
    const dateYear = date.getFullYear();
    const nowYear = now.getFullYear();

    if (dateYear === nowYear && dateMonth === nowMonth && dateDay === nowDay) {
      return 'today';
    }

    // Check if it's yesterday
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    if (dateYear === yesterday.getFullYear() &&
      dateMonth === yesterday.getMonth() &&
      dateDay === yesterday.getDate()) {
      return 'yesterday';
    }

    // Less than a week
    if (diffDays < 7) {
      return `${diffDays} days ago`;
    }

    // Less than a month (30 days)
    if (diffDays < 30) {
      const weeks = Math.floor(diffDays / 7);
      return weeks === 1 ? '1 week ago' : `${weeks} weeks ago`;
    }

    // Less than a year
    if (diffDays < 365) {
      const months = Math.floor(diffDays / 30.44); // Average days in month
      return months === 1 ? '1 month ago' : `${months} months ago`;
    }

    // Years
    const years = Math.floor(diffDays / 365.25); // Account for leap years
    return years === 1 ? '1 year ago' : `${years} years ago`;
  }

  // Workspace deletion methods
  deleteWorkspace(): void {
    this.deleteWorkspaceState.show = true;
  }

  confirmDeleteWorkspace(): void {
    if (this.workspaceId && this.workspace) {
      this.deleteWorkspaceState.isDeleting = true;
      this.store.dispatch(WorkspaceActions.deleteWorkspace({
        id: this.workspaceId
      }));
    }
  }

  cancelDeleteWorkspace(): void {
    this.deleteWorkspaceState = {
      show: false,
      isDeleting: false
    };
  }
}
